import { Box, Dialog, DialogContent } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import LinearProgress from '@mui/material/LinearProgress';
import { makeStyles } from '@mui/styles';
import { useState, useEffect, useRef } from 'react';

import { Button, Alert, Typography, colors } from '@leapeasy/ui-kit';
import leap from 'assets/leap.jpeg';
import disputeSuccessIcon from 'assets/dispute_success_icon.svg';
import {
  setResultAction,
  setInitiateAction,
  setClaimIdAction,
} from 'store/actions/claimFormActions';
import { getClaimDocumentStatus, getClaimSubmissionStatus } from 'store/sagas/APIs/claimUpload';
import { isStagedUploadEnabled } from 'utils/stagedUpload';

const useStyles = makeStyles({
  success_logo: {
    borderRadius: '50px !important',
    width: '250px',
    height: '250px',
    backgroundColor: '#f0c2c254',
    padding: 5,
    marginRight: '23px',
  },
  progress: {
    borderRadius: '50px !important',
    width: '250px',
    height: '10px',
    backgroundColor: '#f0c2c254',
    padding: 5,
    marginRight: '23px',
  },
});

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 120;

const Success = (props) => {
  const classes = useStyles();
  const formData = useSelector((state) => state.getIn(['formData']));
  const dispatch = useDispatch();
  const result = formData?.createResult;
  const [claimFormFlag, setClaimFormFlag] = useState(false);
  const [successLogoSrc, setSuccessLogoSrc] = useState(leap);
  const resultRef = useRef(result);

  useEffect(() => {
    resultRef.current = result;
  }, [result]);

  // Poll document sync status once SF claim ID is known (processing state)
  useEffect(() => {
    if (result?.status !== 'processing' || !result?.id) {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;

    const pollStatus = async () => {
      if (cancelled) {
        return;
      }

      attempts += 1;
      if (attempts > MAX_POLL_ATTEMPTS) {
        const current = resultRef.current;
        dispatch(
          setResultAction({
            ...current,
            status: 'partial_failed',
            documentsUploaded: current.documentsUploaded || 0,
            documentsTotal: current.documentsTotal || 0,
            documentsFailed: 0,
            documentSyncStatus: 'partial_failed',
            message:
              'Document sync is taking longer than expected. Your claim was received — our team will attach any remaining documents.',
          }),
        );
        return;
      }

      try {
        const status = await getClaimDocumentStatus(resultRef.current.id);
        if (cancelled) {
          return;
        }

        const current = resultRef.current;

        if (status.documentSyncStatus === 'complete') {
          dispatch(
            setResultAction({
              ...current,
              status: 'success',
              documentsUploaded: status.uploaded,
              documentsTotal: status.total,
              documentSyncStatus: status.documentSyncStatus,
            }),
          );
          return;
        }

        if (status.documentSyncStatus === 'partial_failed') {
          dispatch(
            setResultAction({
              ...current,
              status: 'partial_failed',
              documentsUploaded: status.uploaded,
              documentsTotal: status.total,
              documentsFailed: status.failed,
              documentSyncStatus: status.documentSyncStatus,
            }),
          );
          return;
        }

        dispatch(
          setResultAction({
            ...current,
            status: 'processing',
            documentsUploaded: status.uploaded,
            documentsTotal: status.total,
            documentSyncStatus: status.documentSyncStatus,
          }),
        );
      } catch (error) {
        if (attempts >= MAX_POLL_ATTEMPTS) {
          const current = resultRef.current;
          dispatch(
            setResultAction({
              ...current,
              status: 'partial_failed',
              documentsUploaded: current.documentsUploaded || 0,
              documentsTotal: current.documentsTotal || 0,
              documentsFailed: 0,
              documentSyncStatus: 'partial_failed',
              message:
                'Unable to confirm document sync status. Your claim was received — contact claims@leapeasy.com if you need help.',
            }),
          );
        }
      }
    };

    pollStatus();
    const intervalId = setInterval(pollStatus, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [result?.status, result?.id, dispatch]);

  // Poll claim submission status when staged accept response received (success with uploadSessionId but no id)
  useEffect(() => {
    if (
      !isStagedUploadEnabled() ||
      result?.status !== 'success' ||
      result?.id ||
      !result?.uploadSessionId
    ) {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;

    const pollSubmission = async () => {
      if (cancelled) {
        return;
      }

      attempts += 1;
      if (attempts > MAX_POLL_ATTEMPTS) {
        return;
      }

      try {
        const status = await getClaimSubmissionStatus(resultRef.current.uploadSessionId);
        if (cancelled) {
          return;
        }

        const current = resultRef.current;

        if (status.submissionStatus === 'created' && status.claimId) {
          dispatch(setClaimIdAction(status.claimName));
          dispatch(
            setResultAction({
              ...current,
              status: 'success',
              id: status.claimId,
              name: status.claimName,
              documentSyncStatus: status.documentSyncStatus,
              documentsUploaded: status.uploaded,
              documentsTotal: status.total,
            }),
          );
          return;
        }

        if (status.submissionStatus === 'failed') {
          dispatch(
            setResultAction({
              status: 'failed',
              message:
                status.error?.message ||
                'Unable to complete your claim submission. Please contact claims@leapeasy.com.',
              data: status.error?.data,
            }),
          );
        }
      } catch {
        // Keep polling while submission is pending
      }
    };

    pollSubmission();
    const intervalId = setInterval(pollSubmission, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [result?.status, result?.id, result?.uploadSessionId, dispatch]);

  const goToFirstPage = () => {
    dispatch(setResultAction(null));
    dispatch(setInitiateAction());
    props.handleBack();
  };

  if (!result) {
    return (
      <Box py={5} display="flex" flexDirection="column" alignItems="center" gap={2}>
        <Alert
          showIcon
          showBorder={false}
          showClose={false}
          type="error"
          variant="contained"
          title="No submission result"
          description="We could not find your claim submission status. Please try again."
        />
        <Button onClick={() => goToFirstPage()} variant="primary">
          Start Over
        </Button>
      </Box>
    );
  }

  const isDuplicate =
    result.status === 'failed' &&
    (result.message === 'isDuplicated' || result.data?.message === 'isDuplicated');
  const duplicateClaim = Array.isArray(result.data?.claimData)
    ? result.data.claimData[0]
    : Array.isArray(result.data)
      ? result.data[0]
      : null;
  const isProcessing = result.status === 'pending' || result.status === 'processing';
  const showActions = !isProcessing;
  const showSuccessContent = result.status === 'success';

  return (
    <Box py={5}>
      <Box display="flex" flexDirection="column" alignItems="center" mb={1}>
        <img
          src={successLogoSrc}
          alt="Leap success"
          className={classes.success_logo}
          onError={() => {
            if (successLogoSrc !== disputeSuccessIcon) {
              setSuccessLogoSrc(disputeSuccessIcon);
            }
          }}
        />
        {isProcessing && (
          <>
            <LinearProgress className={classes.progress} />
            <Typography variant="body1" style={{ marginTop: '12px', color: colors.purple[900] }}>
              {result.status === 'pending'
                ? 'Submitting your claim...'
                : `Claim received — attaching documents (${result.documentsUploaded || 0} of ${result.documentsTotal || 0})...`}
            </Typography>
            {result.status === 'processing' && result.name && (
              <Typography variant="body2" style={{ marginTop: '8px', color: colors.purple[700] }}>
                Claim #{result.name}
              </Typography>
            )}
          </>
        )}
      </Box>

      <Dialog open={claimFormFlag} onClose={() => setClaimFormFlag(false)} maxWidth="md" fullWidth>
        <DialogContent>
          <iframe
            src="https://app.formbricks.com/s/cma1ite8l19kaut012kller2q"
            width="100%"
            height="500px"
            style={{ border: 'none' }}
            title="Formbricks Form"
          ></iframe>
        </DialogContent>
      </Dialog>

      {showSuccessContent && (
        <Box
          display="flex"
          flexDirection="column"
          gap="8px"
          mt={3}
          sx={{ color: colors.purple[900] }}
        >
          <Typography variant="h4" style={{ fontWeight: '500' }}>
            Claim {formData.claimId ? `${formData.claimId} ` : ''}Submitted Successfully!
          </Typography>

          <Typography variant="body1">
            Thank you {formData.data.contactName}, for filing a claim with Leap.
          </Typography>

          <Typography variant="body1">
            We will be reaching out to you shortly at {formData.data.contactEmail} with information
            regarding your claim!
          </Typography>
        </Box>
      )}

      {result.status === 'partial_failed' && (
        <Box mt={3} display="flex" flexDirection="column" gap={2}>
          <Alert
            showIcon
            showBorder={false}
            showClose={false}
            type="warning"
            variant="contained"
            title={`Claim ${result.name || ''} received`}
            description={
              result.message ||
              `Your claim was created but ${result.documentsFailed || 'some'} document(s) could not be attached. Our team has been notified.`
            }
          />
          <Typography variant="body2" style={{ color: colors.neutral[900] }}>
            Please contact <a href="mailto:claims@leapeasy.com">claims@leapeasy.com</a> if you need
            assistance.
          </Typography>
        </Box>
      )}

      {result.status === 'failed' && (
        <>
          {isDuplicate && duplicateClaim ? (
            <Box
              mt={3}
              textAlign="center"
              display="flex"
              flexDirection="column"
              gap="8px"
              sx={{ color: colors.purple[900] }}
            >
              <Typography variant="h4">Oops!</Typography>
              <Typography variant="h4">
                It looks like a claim has already been filed on this policy.
              </Typography>
              <Typography variant="body1">
                Please contact:{' '}
                <a href={'mailto:claims@leapeasy.com?subject=' + formData.data.riderName}>
                  claims@leapeasy.com
                </a>
              </Typography>
              <Typography variant="body1">RiderID: {formData.data.riderName}</Typography>
              <Typography variant="body1">Claim # {duplicateClaim.Name}</Typography>
              <Typography variant="body1">
                Created Date {new Date(duplicateClaim.CreatedDate).toISOString().slice(0, 10)}
              </Typography>
            </Box>
          ) : (
            <Box mt={3} display="flex" flexDirection="column" gap={2}>
              <Alert
                showIcon
                showBorder={false}
                showClose={false}
                type="error"
                variant="contained"
                title="Unable to submit claim"
                description={result.message || result.data?.message || 'Unable to submit claim.'}
              />
              <Typography variant="body2" style={{ color: colors.neutral[900] }}>
                If this issue continues, please contact{' '}
                <a href="mailto:claims@leapeasy.com">claims@leapeasy.com</a>.
              </Typography>
            </Box>
          )}
        </>
      )}

      {showActions && (result.status === 'success' || result.status === 'partial_failed') && (
        <Box display="flex" justifyContent="center" mt={2} gap="16px">
          <Button onClick={() => setClaimFormFlag(true)} variant="primary">
            Leave a review
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Success;
